import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import * as Iap from 'src/replicant/iap';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { getRewards } from 'src/replicant/match2/shopbundles/match2-shopbundles.getters';
import StateObserver, { user } from 'src/StateObserver';
import { options } from 'src/replicant/iap/component/iap.options';
import { waitForVisibilityChange, withLoading } from 'src/lib/utils';
import {
  trackHidePurchaseComplete,
  trackPurchaseSupportReject,
  trackPurchaseSupportRequest,
  trackShowPurchaseComplete,
  trackShowPurchaseCanceled,
  trackShowPurchaseFailed,
} from '../analytics/iap.purchase.complete';
import { showSupportWidget } from 'src/lib/CSWidget';
import { Feature } from 'src/replicant/analytics';
import { hideLoadingAnimation } from 'src/game/animation/loading';
import {
  openPurchaseFailurePopup,
  type PurchaseFailureType,
} from '../iap.purchase.failure.popup';

/**
 * Possible product IDs
 */
type ProductID = keyof (typeof options)['items'];

/**
 * Show rewards popup animation after purchase success
 */
export async function onPurchaseSuccess(
  productId: ProductID,
  opts: {
    feature: string;
    subFeature: string;
  },
) {
  const { feature, subFeature } = opts;
  const now = StateObserver.now();
  const { paidGems = 0, freeGems = 0 } = Iap.utils.getProductRewards(
    productId,
    user(),
    now,
  );

  // Get rewards list, bundle sale has own get rewards method
  const rewardsList = getRewards(productId, now);

  // For some feature we want to skip rewards animations after popup close
  const skipCloseAnimFeatures = [
    'sale',
    'puzzle_booster',
    'match_continue',
    'gacha',
    'piggy_bank',
    'magicLamps',
  ];

  // Prepare rewards list
  const gemsReward = paidGems + freeGems;
  const rewards = gemsReward
    ? ([...rewardsList, { type: 'gems', amount: gemsReward }] as Match2Reward[])
    : rewardsList;

  const { ui } = StateObserver.getState();
  if (ui.loading) hideLoadingAnimation();

  const analytics = {
    productID: productId,
    feature,
    subFeature,
  };

  trackShowPurchaseComplete(analytics);

  // Show rewards popup animation
  await openPopupPromise('popupNewPurchaseComplete', {
    rewards,
    skipShowAnimation: false,
    skipCloseAnimation: skipCloseAnimFeatures.includes(feature),
  });

  trackHidePurchaseComplete(analytics);
}

/**
 * Purchase canceled user experience
 */
export async function onPurchaseCanceled(
  productId: ProductID,
  opts: {
    feature: string;
    subFeature: string;
  },
) {
  const analytics = {
    productID: productId,
    feature: opts.feature,
    subFeature: opts.subFeature,
  };
  // Track purchase canceled popup show analytics
  trackShowPurchaseCanceled(analytics);

  hideLoadingAnimation();

  // Show purchase canceled popup with support button
  const needSupport = await openPopupPromise('popupPurchaseCanceled', {});

  // User rejected support
  if (!needSupport) {
    trackPurchaseSupportReject(analytics, {
      cancellationType: 'cancel',
    });
    return;
  }

  // User requested support
  trackPurchaseSupportRequest(analytics, {
    cancellationType: 'cancel',
  });

  await withLoading(() => showSupportWidget(), {
    feature: Feature.IAP._,
    subFeature: Feature.IAP.SHOW_SUPPORT_WIDGET_ON_CANCEL,
  });
}

/**
 * Purchase failed user experience
 */
export async function onPurchaseFailure(
  productId: ProductID,
  opts: {
    feature: string;
    subFeature: string;
    type: PurchaseFailureType;
  },
) {
  const { type } = opts;
  const analytics = {
    productID: productId,
    feature: opts.feature,
    subFeature: opts.subFeature,
  };

  hideLoadingAnimation();

  // Track purchase failed popup show analytics
  trackShowPurchaseFailed(analytics);

  // Show purchase failed popup with support button
  const needSupport = await openPurchaseFailurePopup(user(), type);

  // User rejected support
  if (!needSupport) {
    trackPurchaseSupportReject(analytics, {
      cancellationType: 'error',
    });
    return;
  }

  // User requested support
  trackPurchaseSupportRequest(analytics, {
    cancellationType: 'error',
  });

  // Show CS widget
  await withLoading(() => showSupportWidget(), {
    feature: Feature.IAP._,
    subFeature: Feature.IAP.SHOW_SUPPORT_WIDGET_ON_ERROR,
  });
}

/**
 * Purchase redirect sequence
 */
export async function onRedirect(
  productID: ProductID,
  opts: {
    redirectUrl: string;
    provider: string;
    method: string;
    purchaseID: string;
    feature: string;
    subFeature: string;
  },
) {
  // Wait for replicant and analytics send last updates
  await StateObserver.flush();

  // Purchase requires a redirect, so navigate to the redirect URL and
  // pick the flow back up when the user re-enters the game
  location.assign(opts.redirectUrl);

  // Wait for the visibility to return.
  await waitForVisibilityChange();
}

/**
 * We have issue with purchase, we didn't any conformation from payment provider
 */
export async function onProblematicPurchase(
  productID: ProductID,
  opts: {
    provider: string;
    method: string;
    purchaseID: string;
    feature: string;
    subFeature: string;
  },
) {
  // TODO: Maybe add some information popup for better user UX
}
